import { z } from "zod";

/**
 * The single contract for Search Filters — the operator-defined filter bar on
 * the storefront tours page.
 *
 * A "filter" is a `facet_types` row; its options are that type's `facet_values`;
 * a tour's selection is `experience_facets`. Three flavours share the table and
 * are told apart by `facet_types.kind`:
 *
 *   builtin  — Duration / Price / Date. Seeded rows carrying NO options; their
 *              `slug` maps to a computed renderer on the storefront. Renameable,
 *              reorderable, hideable — never deletable, never option-editable.
 *   taxonomy — Destination / Activity Type. Options are the agency's existing
 *              destination / activity lists, managed on their own pages.
 *   custom   — operator-defined. Full option CRUD.
 *
 * Everything a tour stores is an option **id**, never a label, which is what
 * makes a rename safe and a delete explicit (see `docs/features/search-filters`).
 */

/** Discriminator on `facet_types`. `info` is the pre-existing "At a Glance"
 *  chip type and never appears in the filter bar. */
/**
 * `post` is the blog's own taxonomy (`blog-categories`): filed on posts, never
 * on tours, so it is absent from Tour Info, the filter bar and the taxonomy
 * pages alike (feature: blog-post-editor).
 */
export const facetKindSchema = z.enum(["taxonomy", "info", "filter", "post"]);
export type FacetKind = z.infer<typeof facetKindSchema>;

/** How a filter row sources its options — drives which renderer the storefront
 *  picks and which affordances the operator gets. */
export const searchFilterSourceSchema = z.enum([
  "builtin",
  "taxonomy",
  "custom",
]);
export type SearchFilterSource = z.infer<typeof searchFilterSourceSchema>;

/**
 * Slugs owned by the platform. Each maps to a storefront renderer that computes
 * its own options, so an operator-created filter may not claim one — the
 * `(agency_id, slug)` unique index would collide, and a custom "Price" filter
 * would silently shadow the real price range.
 */
export const BUILTIN_FILTER_SLUGS = ["duration", "price", "date"] as const;
export const TAXONOMY_FILTER_SLUGS = ["destination", "activity"] as const;
export const RESERVED_FILTER_SLUGS: readonly string[] = [
  ...BUILTIN_FILTER_SLUGS,
  ...TAXONOMY_FILTER_SLUGS,
];

export type BuiltinFilterSlug = (typeof BUILTIN_FILTER_SLUGS)[number];

export function isReservedFilterSlug(slug: string): boolean {
  return RESERVED_FILTER_SLUGS.includes(slug);
}

/**
 * Classify a `facet_types` row for the filter bar.
 *
 * `kind` is the discriminator the column carries; `slug` only refines a
 * `filter` row into the platform's built-ins versus an operator's own. Returns
 * `null` for `info` rows — the "At a Glance" chip types, which are not
 * filter-bar rows at all and must never appear on either surface.
 *
 * Lives in the contract rather than in either API because both read the same
 * rows and must agree: operator-api decides what the dashboard may edit,
 * traveler-api decides what the storefront renders, and a row the two
 * classified differently would be editable on a surface it never appears on.
 */
export function classifySearchFilter(
  kind: string,
  slug: string,
): SearchFilterSource | null {
  if (kind === "taxonomy") return "taxonomy";
  if (kind !== "filter") return null;
  return (BUILTIN_FILTER_SLUGS as readonly string[]).includes(slug)
    ? "builtin"
    : "custom";
}

/**
 * The heading travelers see above a filter.
 *
 * `filterLabel` wins when set because the taxonomy rows are shared with the
 * Destinations / Activity Types pages: renaming "Destinations" to "Where" in
 * the filter bar must not rename the page that manages them, so the bar's
 * override lives in its own column and `label` stays the page's own heading.
 */
export function resolveSearchFilterLabel(row: {
  label: string;
  filterLabel: string | null;
}): string {
  return row.filterLabel ?? row.label;
}

/**
 * Caps. A traveler-facing filter bar stops being usable long before these, and
 * they bound the definition payload the storefront ships on every tours render.
 */
export const SEARCH_FILTER_NAME_MAX = 40;
export const SEARCH_FILTER_OPTION_MAX = 40;
export const MAX_CUSTOM_FILTERS = 25;
export const MAX_OPTIONS_PER_FILTER = 50;

/**
 * Allow-list for filter names and option labels: letters, digits, spaces,
 * hyphen, parentheses. Deliberately stricter than `plainText()` in
 * `facet-type.dto.ts` (which only bans `<` and `>` at 120 chars) — these strings
 * render in the operator dashboard, on tour pages AND in the traveler filter
 * bar, so the allow-list is the first of two XSS layers. Output encoding is the
 * second; neither replaces the other.
 *
 * Note this bans `<`, `>`, `"`, `'`, `&` and `/` outright, unlike the chip
 * VALUES in `facet-type.dto.ts` which legitimately carry `">3,000 m"`. A filter
 * name is a heading, not a measurement.
 */
export const SEARCH_FILTER_TEXT_REGEX = /^[A-Za-z0-9 ()-]+$/;
export const SEARCH_FILTER_TEXT_MESSAGE =
  "Use letters, numbers, spaces, hyphens and parentheses only";

const searchFilterText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} must be ${max} characters or less`)
    .regex(SEARCH_FILTER_TEXT_REGEX, SEARCH_FILTER_TEXT_MESSAGE);

export const searchFilterNameSchema = searchFilterText(
  SEARCH_FILTER_NAME_MAX,
  "Filter name",
);

export const searchFilterOptionSchema = searchFilterText(
  SEARCH_FILTER_OPTION_MAX,
  "Option",
);

/**
 * Trim then drop case-insensitive duplicates, keeping first occurrence (and its
 * casing). Applied inside the wire schema so the client and the API cannot
 * disagree about what "duplicate" means.
 *
 * The operator drawer reads that shared definition but does NOT rely on this
 * transform: `validate-filter.ts` rejects "Easy" alongside "easy" with an inline
 * error rather than sending both and letting one disappear silently. So this is
 * the backstop for every other caller, not the drawer's behaviour.
 */
export function dedupeOptionLabels(labels: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of labels) {
    const label = raw.trim();
    if (label === "") continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/**
 * Form-facing shape — pure validators, no transform, so `zodResolver` sees
 * matching input and output types (same split as `facet-value.dto.ts`).
 */
export const searchFilterFormSchema = z.object({
  label: searchFilterNameSchema,
  options: z
    .array(searchFilterOptionSchema)
    .min(1, "Add at least one option")
    .max(
      MAX_OPTIONS_PER_FILTER,
      `A filter can have at most ${MAX_OPTIONS_PER_FILTER} options`,
    ),
});

/**
 * Wire shape — the same rules plus de-duplication. `min(1)` is re-checked
 * AFTER the dedupe: a payload of `["Easy", "easy"]` is one option, and
 * `["  ", ""]` is none at all.
 */
export const createSearchFilterSchema = searchFilterFormSchema
  .transform((value) => ({
    label: value.label,
    options: dedupeOptionLabels(value.options),
  }))
  .refine((value) => value.options.length > 0, {
    message: "Add at least one option",
    path: ["options"],
  });

/**
 * Editing a filter replaces its whole option list in one call, because that is
 * how the drawer saves — the operator edits, adds, removes and reorders, then
 * hits Save once. The server diffs against the stored options by **id**, so an
 * option carried through with a new label is a rename (tours keep it) and an
 * option absent from the list is a delete (tours holding it are cleared).
 *
 * `id: null` means "new option" — the server mints one.
 */
export const searchFilterOptionInputSchema = z.object({
  id: z.string().min(1).nullable(),
  label: searchFilterOptionSchema,
});

export const updateSearchFilterSchema = z.object({
  label: searchFilterNameSchema,
  /**
   * Omitted for `builtin` and `taxonomy` filters, which have no editable option
   * list — sending one is a 422 rather than a silently ignored field.
   */
  options: z
    .array(searchFilterOptionInputSchema)
    .max(
      MAX_OPTIONS_PER_FILTER,
      `A filter can have at most ${MAX_OPTIONS_PER_FILTER} options`,
    )
    .optional(),
});

export const reorderSearchFiltersSchema = z.object({
  orderedIds: z.array(z.string().min(1)).min(1),
});

export const setSearchFilterVisibleSchema = z.object({
  visible: z.boolean(),
});

/** One selectable option, as the operator dashboard reads it. */
export const searchFilterOptionDtoSchema = z.object({
  id: z.string().min(1),
  /** Immutable once minted — the traveler URL and every inbound link carry it,
   *  so a rename changes `label` alone. */
  slug: z.string().min(1),
  label: z.string().min(1),
});

export const searchFilterDtoSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  source: searchFilterSourceSchema,
  /** What travelers see above the filter — the operator's override when set,
   *  otherwise the underlying type's own name. Resolved server-side. */
  label: z.string().min(1),
  visible: z.boolean(),
  displayOrder: z.number().int(),
  /** Empty for `builtin` (options are computed) and for `taxonomy` (managed on
   *  the Destinations / Activity Types pages). */
  options: z.array(searchFilterOptionDtoSchema),
  /** Tours carrying at least one of this filter's options. Drives the delete
   *  confirmation's "this removes it from N tours". Only the list response
   *  carries it; treat `undefined` as "not loaded", never as zero. */
  usedOnTours: z.number().int().nonnegative().optional(),
});

/**
 * One tour's selection for one custom filter, in BOTH directions: the tour
 * builder posts the set it renders with the rest of Basics, and the Basics read
 * returns the tour's current set so the panel can prefill (AC-12).
 *
 * `SaveExperienceBasicsService` replaces assignments only within the filter
 * types the payload names (`syncWithinTypes`), so omitting a filter preserves
 * it and sending it with an empty `valueIds` clears it. A Basics payload that
 * omits `searchFilters` entirely therefore leaves every assignment alone
 * (AC-06) — which is what lets a client that has no filter panel, or whose
 * panel has not loaded, save Basics safely.
 */
export const searchFilterAssignmentSchema = z.object({
  facetTypeId: z.string().min(1),
  valueIds: z.array(z.string().min(1)).max(MAX_OPTIONS_PER_FILTER),
});

export const searchFilterAssignmentListSchema = z
  .array(searchFilterAssignmentSchema)
  .max(MAX_CUSTOM_FILTERS);

/**
 * Agency-wide app settings, stored in `agency_app_settings` under the
 * `search-filters` slug. One field: the master switch behind AC-08. Turning it
 * off leaves travelers a plain tour list while every filter, option and tour
 * assignment stays saved — which is why this is a setting and not a delete.
 *
 * Defaults to `true`: installing the app is itself the opt-in, so an operator
 * who installs it and never opens the settings gets the filter bar.
 */
export const searchFiltersSettingsSchema = z.object({
  filterBarEnabled: z.boolean().default(true),
});
export type SearchFiltersSettingsDto = z.infer<
  typeof searchFiltersSettingsSchema
>;

export type SearchFilterFormValues = z.infer<typeof searchFilterFormSchema>;
export type CreateSearchFilterDto = z.infer<typeof createSearchFilterSchema>;
export type UpdateSearchFilterDto = z.infer<typeof updateSearchFilterSchema>;
export type SearchFilterOptionInput = z.infer<
  typeof searchFilterOptionInputSchema
>;
export type ReorderSearchFiltersDto = z.infer<
  typeof reorderSearchFiltersSchema
>;
export type SetSearchFilterVisibleDto = z.infer<
  typeof setSearchFilterVisibleSchema
>;
export type SearchFilterOptionDto = z.infer<typeof searchFilterOptionDtoSchema>;
export type SearchFilterDto = z.infer<typeof searchFilterDtoSchema>;
export type SearchFilterAssignmentDto = z.infer<
  typeof searchFilterAssignmentSchema
>;
